import React, { useEffect, useMemo } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { TextStyle, FontSize } from '@tiptap/extension-text-style';
import { FontFamily } from '@tiptap/extension-font-family';
import { Bold as BoldIcon, Type } from 'lucide-react';

/**
 * DescriptionEditor — iter127
 *
 * Rich-text editor for advertisement descriptions built on TipTap.
 * Supports Bold, Font family, Font size. Emits sanitised HTML via `onChange`.
 * The parent component treats the HTML string as the `description` value and
 * ships it to the backend, which sanitises again server-side before storing.
 *
 * Rendering on the read-side (ListingDetail) uses `dangerouslySetInnerHTML`
 * after a DOMPurify pass so the same formatting appears identically for
 * buyers.
 */

const FONT_FAMILIES = [
  { label: 'Default', value: '' },
  { label: 'Sans (Inter)', value: 'Inter, ui-sans-serif, system-ui, sans-serif' },
  { label: 'Serif', value: 'ui-serif, Georgia, Cambria, serif' },
  { label: 'Mono', value: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace' },
];

const FONT_SIZES = [
  { label: 'Small', value: '13px' },
  { label: 'Normal', value: '' },
  { label: 'Large', value: '18px' },
  { label: 'X-Large', value: '22px' },
];

export const DescriptionEditor = ({
  value,
  onChange,
  placeholder = 'Describe your item — condition, features, why you\'re selling…',
  testId = 'description-editor',
}) => {
  const extensions = useMemo(
    () => [
      StarterKit.configure({
        // Keep the mark set focused — only Bold is exposed in the toolbar.
        heading: false,
        codeBlock: false,
        blockquote: false,
        horizontalRule: false,
        strike: false,
        code: false,
      }),
      TextStyle,
      FontSize,
      FontFamily.configure({ types: ['textStyle'] }),
    ],
    [],
  );

  const editor = useEditor({
    extensions,
    content: value || '',
    editorProps: {
      attributes: {
        'data-testid': testId,
        class:
          'min-h-[140px] max-h-[420px] overflow-y-auto rounded-b-2xl border border-t-0 border-gray-200 px-4 py-3 text-sm text-[#0A0B10] leading-relaxed focus:outline-none focus:ring-4 focus:ring-[#1800ad]/10 [&_p]:my-1',
      },
    },
    onUpdate: ({ editor: ed }) => {
      const html = ed.getHTML();
      // Represent an empty editor as an empty string so backend "required"
      // validation still works. TipTap emits <p></p> for an empty doc.
      onChange?.(html === '<p></p>' ? '' : html);
    },
  });

  // Keep the editor content in sync when the parent's value changes externally
  // (e.g. EditListing hydrating a stored description). Skip when the current
  // content already matches to avoid a caret-reset loop.
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const next = value || '';
    if (current === next) return;
    if (!next && current === '<p></p>') return;
    editor.commands.setContent(next, false);
  }, [editor, value]);

  if (!editor) {
    return (
      <div className="rounded-2xl border border-gray-200 h-[172px] bg-gray-50 animate-pulse" data-testid={`${testId}-loading`} />
    );
  }

  const currentFontSize = editor.getAttributes('textStyle').fontSize || '';
  const currentFontFamily = editor.getAttributes('textStyle').fontFamily || '';

  return (
    <div className="rounded-2xl overflow-hidden">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 rounded-t-2xl border border-gray-200 bg-gray-50 px-2.5 py-2" data-testid={`${testId}-toolbar`}>
        <button
          type="button"
          data-testid={`${testId}-bold-btn`}
          aria-pressed={editor.isActive('bold')}
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${
            editor.isActive('bold')
              ? 'bg-[#1800ad] text-white'
              : 'bg-white text-[#0A0B10] border border-gray-200 hover:bg-gray-100'
          }`}
          title="Bold"
        >
          <BoldIcon size={15} />
        </button>

        <div className="flex items-center gap-1.5">
          <Type size={14} className="text-[#6B7280]" />
          <select
            data-testid={`${testId}-font-family-select`}
            value={currentFontFamily}
            onChange={(e) => {
              const v = e.target.value;
              if (v) editor.chain().focus().setFontFamily(v).run();
              else editor.chain().focus().unsetFontFamily().run();
            }}
            className="text-xs rounded-lg border border-gray-200 bg-white px-2 py-1.5 outline-none focus:ring-2 focus:ring-[#1800ad]/20"
          >
            {FONT_FAMILIES.map((f) => (
              <option key={f.label} value={f.value}>{f.label}</option>
            ))}
          </select>
        </div>

        <select
          data-testid={`${testId}-font-size-select`}
          value={currentFontSize}
          onChange={(e) => {
            const v = e.target.value;
            if (v) editor.chain().focus().setFontSize(v).run();
            else editor.chain().focus().unsetFontSize().run();
          }}
          className="text-xs rounded-lg border border-gray-200 bg-white px-2 py-1.5 outline-none focus:ring-2 focus:ring-[#1800ad]/20"
        >
          {FONT_SIZES.map((s) => (
            <option key={s.label} value={s.value}>{s.label}</option>
          ))}
        </select>
      </div>

      <EditorContent editor={editor} placeholder={placeholder} />
      {editor.isEmpty && (
        <p className="text-[11px] text-[#9CA3AF] mt-1 px-1" data-testid={`${testId}-hint`}>
          Tip: Highlight text and use the toolbar to make it Bold or change the font.
        </p>
      )}
    </div>
  );
};

export default DescriptionEditor;
